import type { GuidelineConfig } from '@atlaskit/editor-common/guideline/types';
import type { EditorAppearance } from '@atlaskit/editor-common/types/editor-appearance';
import {
	akEditorFullWidthLayoutWidth,
	akEditorFullPageNarrowBreakout,
	akEditorGutterPadding,
	akEditorGutterPaddingDynamic,
	akEditorGutterPaddingReduced,
	akEditorMaxWidthLayoutWidth,
} from '@atlaskit/editor-shared-styles/constants';
import type { AlignmentValue } from '@atlaskit/native-embeds-common';
import { editorExperiment } from '@atlaskit/tmp-editor-statsig/editor-experiment';

const calcLayoutMaxWidth = (
	containerWidth: number,
	layoutMaxWidth: number,
	editorAppearance?: EditorAppearance,
): number => {
	const fullPagePadding =
		editorAppearance === 'full-page' &&
		containerWidth <= akEditorFullPageNarrowBreakout &&
		editorExperiment('platform_editor_preview_panel_responsiveness', true, {
			exposure: true,
		})
			? akEditorGutterPaddingReduced
			: akEditorGutterPaddingDynamic();

	const fullWidthPadding =
		editorAppearance === 'full-page' ? fullPagePadding * 2 : akEditorGutterPadding * 2;
	return Math.min(containerWidth - fullWidthPadding, layoutMaxWidth);
};

export const calcMaxWidth = (containerWidth: number, editorAppearance?: EditorAppearance): number =>
	calcLayoutMaxWidth(containerWidth, akEditorFullWidthLayoutWidth, editorAppearance);

export const calcMaxLayoutWidth = (containerWidth: number): number => {
	const gutterPadding =
		containerWidth <= akEditorFullPageNarrowBreakout &&
		editorExperiment('platform_editor_preview_panel_responsiveness', true, {
			exposure: true,
		})
			? akEditorGutterPaddingReduced
			: akEditorGutterPaddingDynamic();

	return Math.min(
		containerWidth - gutterPadding * 2 - akEditorGutterPadding,
		akEditorMaxWidthLayoutWidth,
	);
};

export const getNativeEmbedMaxWidthGuidelines = (maxWidth: number): GuidelineConfig[] => {
	const guidelineOffset = maxWidth / 2;

	return [
		{
			key: 'max_width_left',
			position: {
				x: -guidelineOffset,
			},
		},
		{
			key: 'max_width_right',
			position: {
				x: guidelineOffset,
			},
		},
	];
};

// ---------------------------------------------------------------------------
// Shared constants used by ResizableNativeEmbed coordinator + EmbedResizeOverlay
// ---------------------------------------------------------------------------

// TODO: CNS-24595 - use the constants file.
export const MIN_WIDTH = 200;
export const MIN_HEIGHT = 200;
export const MAX_HEIGHT = 748;
export const SNAP_GAP = 8;
export const CSS_VAR_NATIVE_EMBED_WIDTH = '--native-embed-width';

export const resizerNextTestId = 'nativeEmbed.resizerNext.testid';

/**
 * Maps AlignmentValue to RichMediaLayout for guideline snapping.
 * AlignmentValue uses 'left'/'right', but RichMediaLayout expects 'align-start'/'align-end'.
 */
export const mapAlignmentToLayout = (
	alignment: AlignmentValue,
): 'align-start' | 'align-end' | 'center' | 'wrap-left' | 'wrap-right' => {
	if (alignment === 'left') {
		return 'align-start';
	}
	if (alignment === 'right') {
		return 'align-end';
	}
	// 'center', 'wrap-left', 'wrap-right' pass through unchanged
	return alignment;
};
